import { Canvas, CanvasAndUser } from '@ritabygglov/core';
import { API_BASE_URL } from '../config';
import { UserData } from '../context/User/useUser';
import createHttpClient, { handleBlobResponse } from './HttpClient';

function createCanvasService(baseURL: string) {
  const httpClient = createHttpClient(baseURL);

  return {
    createCanvas: async (
      canvas: Pick<Canvas, 'name'>
    ): Promise<{ message: string; canvas?: Canvas }> => {
      return httpClient.post('/canvases', canvas);
    },
    deleteCanvas: async (canvasId: string): Promise<{ message: string }> => {
      return httpClient.delete(`/canvases/${canvasId}`);
    },
    copyCanvas: async (
      canvasId: string
    ): Promise<{ message: string; canvas: Canvas }> => {
      return httpClient.post<
        { message: string; canvas: Canvas },
        Record<string, never>
      >(`/canvases/${canvasId}/copy`, {});
    },
    editCanvas: async (
      canvasId: string,
      canvas: Partial<Canvas>
    ): Promise<{ message: string; canvas?: Canvas }> => {
      return httpClient.put(`/canvases/${canvasId}`, canvas);
    },
    exportCanvas: async (canvasId: string): Promise<Blob> => {
      return httpClient.post(
        `/canvases/${canvasId}/export`,
        undefined,
        undefined,
        handleBlobResponse
      );
    },
    uploadMap: async (
      canvasId: string,
      image: Blob | File,
      fileName = `mapbox-${Date.now()}.png`,
      page?: number,
      rotation?: number
    ): Promise<null> => {
      const form = new FormData();
      form.append('file', image, fileName);
      if (page !== undefined) form.append('page', String(page));
      if (rotation !== undefined) form.append('rotation', String(rotation));

      return httpClient.post(`/canvases/${canvasId}/upload-map`, image, {
        headers: {},
        body: form,
      });
    },
    getExportCanvas: async (
      canvasId: string,
      token: string | null
    ): Promise<Canvas[]> => {
      const params = new URLSearchParams();
      if (token) params.append('token', token);

      return httpClient.get(
        `/canvases/${canvasId}/secret-export-route?${params.toString()}`
      );
    },
    getCanvases: async (
      queryParams?: string
    ): Promise<{ projects: CanvasAndUser[]; totalCount: number }> => {
      const params =
        queryParams ||
        new URLSearchParams({ page: '1', limit: '100' }).toString();
      return httpClient.get(`/admin/projects?${params}`);
    },
    getUserProjects: async (
      userId: number
    ): Promise<{ user: UserData; projects: CanvasAndUser[] }> => {
      return httpClient.get(`/admin/users/${userId}/projects`);
    },
    garbageCollectCanvas: async (
      canvasId: string
    ): Promise<{ message: string; deletedChanges: number }> => {
      return httpClient.delete(`/admin/canvases/${canvasId}/garbage-collect`);
    },
    adminCopyCanvas: async (
      canvasId: string,
      targetUserId?: number
    ): Promise<{ message: string; canvas: Canvas }> => {
      return httpClient.post<
        { message: string; canvas: Canvas },
        { targetUserId?: number }
      >(`/admin/canvases/${canvasId}/copy`, { targetUserId });
    },
  };
}

export const canvasService = createCanvasService(API_BASE_URL!);
